import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { DataService } from '../../data.service';
import { EtlapSor, helyiNap, hetHetfoje, napHozzaad } from './etlap-kozos';

/**
 *  ÉTLAP FELTÖLTÉSE ÉS SZERKESZTÉSE  ·  Admin → Étkezés
 *
 *  A beszállító heti Excelje (a „Megrendelő” lap: dátum, nap, „"A" Menü”, étel) →
 *  ELŐNÉZET → Feltöltés. Az előnézet semmit nem ír: megmutatja, mit olvasott ki a
 *  rendszer, melyik rendelhető menühöz párosította, és mire gyanakszik (dátum és napnév
 *  eltér, hétvégi nap, ismétlődő sor). A Feltöltés a fájlban szereplő NAPOKAT cseréli –
 *  egy javított fájl újrafeltöltése így nem duplikál.
 *
 *  Alatta a következő hetek étlapja, soronként javítható és törölhető, és egy-egy nap
 *  kézzel is felvehető (ha a beszállító e-mailben szól, hogy kedden más lesz).
 */
@Component({
  selector: 'app-etlap-admin',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './etlap-admin.component.html',
  styleUrls: ['./etlap.component.scss'],
})
export class EtlapAdminComponent implements OnInit, OnChanges {
  @Input() intezmeny = '';

  helyek: any[] = [];
  hely: string | null = null;

  fajl: File | null = null;
  elonezet: any = null;
  fut = false;
  ment = false;

  lista: EtlapSor[] = [];
  listaFut = false;
  /** Hány hét látszik a mostanitól (a múlt hét is, hogy a pénteki javítás elérhető legyen). */
  hetekSzama = 4;
  uj: { datum: string; menu_nev: string; leiras: string } = { datum: helyiNap(new Date()), menu_nev: '', leiras: '' };

  constructor(private http: HttpClient, private data: DataService) {}

  ngOnInit() { this.indul(); }

  ngOnChanges(v: SimpleChanges) {
    if (v['intezmeny'] && !v['intezmeny'].firstChange) this.indul();
  }

  private indul() {
    if (!this.intezmeny) return;
    this.http.get<any[]>('/api/etkezes/hely/' + this.intezmeny).subscribe({
      next: (r) => {
        this.helyek = Array.isArray(r) ? r : [];
        if (this.helyek.length === 1) this.hely = String(this.helyek[0].id);
      },
      error: () => { this.helyek = []; },
    });
    this.listaBetolt();
  }

  /* ─────────── Feltöltés ─────────── */

  fajlValasztas(ev: any) {
    const f: File | undefined = ev?.target?.files?.[0];
    this.fajl = f || null;
    this.elonezet = null;
    if (this.fajl) this.elonezetKer();
  }

  /** Beszállító-váltás után az előnézet újrapárosít (más beszállító, más menük). */
  helyValtas(ertek: any) {
    this.hely = ertek ? String(ertek) : null;
    if (this.fajl) this.elonezetKer();
    this.listaBetolt();
  }

  elonezetKer() {
    if (!this.fajl) return;
    const fd = new FormData();
    fd.append('file', this.fajl, this.fajl.name);
    fd.append('mod', 'elonezet');
    if (this.hely) fd.append('hely', this.hely);
    this.fut = true;
    this.http.post<any>('/api/etkezes-etlap/import/' + this.intezmeny, fd).subscribe({
      next: (r) => {
        this.fut = false;
        this.elonezet = r;
        //  Ha a kiszolgáló a fájlból döntötte el a beszállítót, a választó is kövesse.
        if (!this.hely && r?.hely) this.hely = String(r.hely);
      },
      error: (e) => {
        this.fut = false;
        this.elonezet = null;
        this.data.sendToast(e?.error?.error || 'A fájl nem dolgozható fel.', 'error');
      },
    });
  }

  get tobbHely(): boolean { return this.helyek.length > 1; }

  get elonezetNapok(): number {
    return new Set((this.elonezet?.sorok || []).map((s: any) => s.datum)).size;
  }

  get feltoltheto(): boolean {
    return !!this.elonezet?.sorok?.length && !this.ment && (!this.tobbHely || !!this.hely);
  }

  feltoltes() {
    if (!this.feltoltheto) return;
    const napok = this.elonezetNapok;
    if (!confirm(`Feltöltöd az étlapot? ${napok} nap meglévő étlapja (ennél a beszállítónál) lecserélődik.`)) return;
    this.ment = true;
    this.http.post<any>('/api/etkezes-etlap/mentes/' + this.intezmeny, {
      hely: this.hely,
      sorok: (this.elonezet.sorok || []).map((s: any) => ({ datum: s.datum, menu_nev: s.menu_nev, leiras: s.leiras })),
    }).subscribe({
      next: (r) => {
        this.ment = false;
        this.data.sendToast(`Étlap feltöltve: ${r.napok} nap, ${r.irt} menü`
          + (r.torolt ? ` (${r.torolt} korábbi bejegyzés lecserélve)` : '') + '.', 'success');
        this.elonezet = null;
        this.fajl = null;
        this.listaBetolt();
      },
      error: (e) => {
        this.ment = false;
        this.data.sendToast(e?.error?.error || 'A feltöltés nem sikerült.', 'error');
      },
    });
  }

  megse() {
    this.elonezet = null;
    this.fajl = null;
  }

  /* ─────────── A következő hetek étlapja ─────────── */

  listaBetolt() {
    if (!this.intezmeny) return;
    const tol = napHozzaad(hetHetfoje(new Date()), -7);
    const ig = napHozzaad(tol, this.hetekSzama * 7 + 6);
    const params: any = { tol: helyiNap(tol), ig: helyiNap(ig) };
    if (this.hely) params.hely = this.hely;
    this.listaFut = true;
    this.http.get<any>('/api/etkezes-etlap/lista/' + this.intezmeny, { params }).subscribe({
      next: (r) => {
        this.lista = (r?.sorok || []).map((s: EtlapSor) => ({ ...s, _leiras: s.leiras, _menu_nev: s.menu_nev }));
        this.listaFut = false;
      },
      error: (e) => {
        this.listaFut = false;
        this.data.sendToast(e?.error?.error || 'Az étlap nem tölthető be.', 'error');
      },
    });
  }

  /** Új nap kezdődik-e a listában ennél a sornál (napfejléc kirajzolásához). */
  ujNap(i: number): boolean {
    return i === 0 || this.lista[i - 1]?.datum !== this.lista[i]?.datum;
  }

  napCim(s: EtlapSor): string {
    const [e, h, n] = String(s.datum).split('-');
    const nap = s.nap ? s.nap.charAt(0).toUpperCase() + s.nap.slice(1) : '';
    return `${e}. ${h}. ${n}. ${nap}`;
  }

  valtozott(s: any): boolean {
    return s._leiras !== s.leiras || s._menu_nev !== s.menu_nev;
  }

  sorMentes(s: any) {
    if (!String(s._leiras || '').trim() || !String(s._menu_nev || '').trim()) {
      this.data.sendToast('A menü és az étel sem lehet üres – törléshez használd a kuka gombot.', 'warning');
      return;
    }
    this.http.put<any>(`/api/etkezes-etlap/sor/${s.id}/${this.intezmeny}`, { leiras: s._leiras, menu_nev: s._menu_nev })
      .subscribe({
        next: () => { this.data.sendToast('Étlap-sor mentve.', 'success'); this.listaBetolt(); },
        error: (e) => this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error'),
      });
  }

  sorTorles(s: EtlapSor) {
    if (!confirm(`Törlöd a(z) ${s.datum} „${s.menu_nev}” étlap-sort?`)) return;
    this.http.delete<any>(`/api/etkezes-etlap/sor/${s.id}/${this.intezmeny}`).subscribe({
      next: () => { this.data.sendToast('Étlap-sor törölve.', 'success'); this.listaBetolt(); },
      error: (e) => this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error'),
    });
  }

  ujSor() {
    const u = this.uj;
    if (!u.datum || !u.menu_nev.trim() || !u.leiras.trim()) {
      this.data.sendToast('Add meg a napot, a menüt (pl. A) és az ételt.', 'warning');
      return;
    }
    if (this.tobbHely && !this.hely) {
      this.data.sendToast('Válaszd ki a beszállítót.', 'warning');
      return;
    }
    this.http.post<any>('/api/etkezes-etlap/sor/' + this.intezmeny, { ...u, hely: this.hely }).subscribe({
      next: (r) => {
        this.data.sendToast(r?.felulirva ? 'A nap menüje felülírva.' : 'Étlap-sor felvéve.', 'success');
        this.uj = { datum: u.datum, menu_nev: '', leiras: '' };
        this.listaBetolt();
      },
      error: (e) => this.data.sendToast(e?.error?.error || 'A felvétel nem sikerült.', 'error'),
    });
  }

  trackSor = (_: number, s: any) => s.id || `${s.datum}|${s.menu_nev}|${s.sor}`;
}
